const styles = `
  :host {
    display: block;
  }

  button {
    position: relative;
    width: 100%;
    min-height: 128px;
    overflow: hidden;
    border: 1px solid rgba(247, 239, 218, 0.16);
    border-radius: 8px;
    padding: 12px;
    color: #f7efda;
    background:
      linear-gradient(135deg, rgba(167,245,206,0.18), transparent 38%),
      linear-gradient(180deg, rgba(247,239,218,0.12), rgba(247,239,218,0.03)),
      #0c100d;
    box-shadow: 0 16px 34px rgba(0,0,0,0.34);
    text-align: left;
  }

  button::before,
  button::after {
    content: "";
    position: absolute;
    pointer-events: none;
  }

  button::before {
    inset: 0;
    background:
      linear-gradient(112deg, transparent 0 34%, rgba(255,255,255,0.18) 46%, transparent 60%),
      repeating-linear-gradient(90deg, rgba(255,255,255,0.038) 0 1px, transparent 1px 13px);
    transform: translateX(-28%);
    opacity: 0.72;
  }

  button::after {
    right: 12px;
    top: 12px;
    bottom: 12px;
    width: 100px;
    border-radius: 8px;
    background:
      linear-gradient(135deg, #a7f5ce, #68c7ff 46%, #f5c76b),
      #a7f5ce;
    opacity: 0.95;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.24), 0 12px 24px rgba(0,0,0,0.22);
    transform: rotate(3deg);
  }

  button:not(:disabled):active {
    transform: translateY(1px) scale(0.992);
  }

  button:not(:disabled):hover::before {
    animation: pack-sheen 1.4s ease;
  }

  button[disabled] {
    opacity: 0.58;
    box-shadow: none;
  }

  .inner {
    position: relative;
    z-index: 1;
    min-height: 104px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 94px;
    gap: 12px;
    align-items: center;
  }

  .copy {
    min-width: 0;
    display: grid;
    gap: 7px;
  }

  .eyebrow {
    color: #a7f5ce;
    font: 900 0.7rem/1 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
    letter-spacing: 0;
  }

  .title {
    color: #fff8e8;
    font: 900 1.22rem/1 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
    letter-spacing: 0;
  }

  .meta {
    max-width: 26ch;
    color: rgba(247, 239, 218, 0.7);
    font-size: 0.84rem;
    line-height: 1.32;
  }

  .pack {
    position: relative;
    z-index: 1;
    width: 86px;
    height: 104px;
    justify-self: end;
    border-radius: 8px;
    background:
      linear-gradient(160deg, rgba(255,255,255,0.36), transparent 26%),
      linear-gradient(135deg, #f5c76b, #a7f5ce 45%, #68c7ff),
      #a7f5ce;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.28), 0 14px 24px rgba(0,0,0,0.28);
    transform: rotate(3deg);
    overflow: hidden;
  }

  .pack::before,
  .pack::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 12px;
    background:
      repeating-linear-gradient(90deg, rgba(8,16,12,0.28) 0 8px, rgba(255,255,255,0.2) 8px 16px);
  }

  .pack::before {
    top: 0;
  }

  .pack::after {
    bottom: 0;
  }

  .pack-mark {
    position: absolute;
    inset: 24px 12px;
    display: grid;
    place-items: center;
    border-radius: 7px;
    color: #07100c;
    background: rgba(255,255,255,0.62);
    font: 900 0.78rem/1 var(--font-display, "Sora", sans-serif);
  }

  .pack-mark::before {
    content: "";
    width: 30px;
    height: 22px;
    border-radius: 50% 50% 8px 50%;
    background: #07100c;
    transform: rotate(-28deg);
  }

  .loading {
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: 10px;
    height: 4px;
    overflow: hidden;
    border-radius: 999px;
    background: rgba(247,239,218,0.14);
  }

  .loading span {
    display: block;
    width: 44%;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #a7f5ce, #f5c76b);
    animation: loading-slide 880ms ease-in-out infinite;
  }

  @keyframes pack-sheen {
    from {
      transform: translateX(-42%);
    }

    to {
      transform: translateX(42%);
    }
  }

  @keyframes loading-slide {
    0%, 100% {
      transform: translateX(0);
    }

    50% {
      transform: translateX(126%);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    button:not(:disabled):hover::before,
    .loading span {
      animation: none;
    }
  }
`;

export class PackButtonElement extends HTMLElement {
  private readonly root: ShadowRoot;
  private loading = false;
  private remaining = 0;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
  }

  setState({ loading, remaining }: { loading: boolean; remaining: number }): void {
    this.loading = loading;
    this.remaining = remaining;
    this.render();
  }

  private render(): void {
    const locked = this.remaining <= 0;
    const title = this.loading ? 'Warming reveal' : locked ? 'Vault empty' : 'Rip field pack';
    const meta = this.loading
      ? 'Preloading the lead specimen and stacking the booster.'
      : locked
        ? 'No complete packs remain until local midnight.'
        : `${this.remaining} complete ${this.remaining === 1 ? 'pack' : 'packs'} left today.`;

    this.root.innerHTML = `
      <style>${styles}</style>
      <button type="button" ${this.loading || locked ? 'disabled' : ''} aria-label="${escapeAttribute(title)}">
        <div class="inner">
          <div class="copy">
            <span class="eyebrow">Sealed booster</span>
            <span class="title">${title}</span>
            <span class="meta">${meta}</span>
          </div>
          <span class="pack" aria-hidden="true"><span class="pack-mark"></span></span>
        </div>
        ${this.loading ? '<span class="loading" aria-hidden="true"><span></span></span>' : ''}
      </button>
    `;

    this.root.querySelector('button')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('press', { bubbles: true, composed: true }));
    });
  }
}

if (!customElements.get('pack-button')) {
  customElements.define('pack-button', PackButtonElement);
}

function escapeAttribute(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}
